/**
 * AST -> executable closure tree.
 *
 * PRD §6.5 forbids `eval`/`new Function` on anything sourced from a project
 * file, because sharing `.json` projects is an advertised feature and codegen
 * would turn "open a colleague's project" into arbitrary script execution in an
 * origin holding all of the user's other work.
 *
 * Compiling to a tree of monomorphic closures keeps that guarantee while
 * staying fast: V8 inlines these well, and the arithmetic is the same. The cost
 * versus generated code is roughly 2-3x, which the §6.1 throughput budget
 * absorbs; the security property is not negotiable, so this is the trade made.
 */

import type { Node } from './ast.ts';

/** A compiled expression reads its inputs from a dense environment vector. */
export type Compiled = (env: Float64Array) => number;

export class CompileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CompileError';
  }
}

/**
 * Compile `node` against a name -> environment-index mapping.
 *
 * Every referenced name must be present in `slots`; resolution happens once at
 * compile time so the hot path never touches a string or a hash lookup.
 */
export function compile(node: Node, slots: ReadonlyMap<string, number>): Compiled {
  switch (node.kind) {
    case 'num': {
      const v = node.value;
      return () => v;
    }

    case 'ref': {
      const idx = slots.get(node.name);
      if (idx === undefined) {
        throw new CompileError(`Unknown name '${node.name}'`);
      }
      return (env) => env[idx];
    }

    case 'neg': {
      const a = compile(node.operand, slots);
      return (env) => -a(env);
    }

    case 'binary': {
      const l = compile(node.left, slots);
      const r = compile(node.right, slots);
      switch (node.op) {
        case '+':
          return (env) => l(env) + r(env);
        case '-':
          return (env) => l(env) - r(env);
        case '*':
          return (env) => l(env) * r(env);
        case '/':
          return (env) => l(env) / r(env);
        case '^':
          return (env) => Math.pow(l(env), r(env));
      }
      break;
    }

    case 'compare': {
      const l = compile(node.left, slots);
      const r = compile(node.right, slots);
      // Booleans surface as 1/0 so comparisons compose with arithmetic.
      switch (node.op) {
        case '<':
          return (env) => (l(env) < r(env) ? 1 : 0);
        case '<=':
          return (env) => (l(env) <= r(env) ? 1 : 0);
        case '>':
          return (env) => (l(env) > r(env) ? 1 : 0);
        case '>=':
          return (env) => (l(env) >= r(env) ? 1 : 0);
        case '==':
          return (env) => (l(env) === r(env) ? 1 : 0);
        case '!=':
          return (env) => (l(env) !== r(env) ? 1 : 0);
      }
      break;
    }

    case 'if': {
      const c = compile(node.cond, slots);
      const t = compile(node.then, slots);
      const f = compile(node.otherwise, slots);
      return (env) => (c(env) !== 0 ? t(env) : f(env));
    }

    case 'call': {
      const args = node.args.map((a) => compile(a, slots));
      const [a0, a1, a2] = args;
      switch (node.name) {
        case 'sqrt':
          return (env) => Math.sqrt(a0(env));
        case 'exp':
          return (env) => Math.exp(a0(env));
        case 'ln':
          return (env) => Math.log(a0(env));
        case 'log10':
          return (env) => Math.log10(a0(env));
        case 'abs':
          return (env) => Math.abs(a0(env));
        case 'sin':
          return (env) => Math.sin(a0(env));
        case 'cos':
          return (env) => Math.cos(a0(env));
        case 'tan':
          return (env) => Math.tan(a0(env));
        case 'asin':
          return (env) => Math.asin(a0(env));
        case 'acos':
          return (env) => Math.acos(a0(env));
        case 'atan':
          return (env) => Math.atan(a0(env));
        case 'atan2':
          return (env) => Math.atan2(a0(env), a1(env));
        case 'floor':
          return (env) => Math.floor(a0(env));
        case 'ceil':
          return (env) => Math.ceil(a0(env));
        case 'round':
          return (env) => Math.round(a0(env));
        case 'min':
          return (env) => Math.min(a0(env), a1(env));
        case 'max':
          return (env) => Math.max(a0(env), a1(env));
        case 'clamp':
          return (env) => Math.min(Math.max(a0(env), a1(env)), a2(env));
      }
      break;
    }
  }

  throw new CompileError(`Unsupported node ${(node as { kind: string }).kind}`);
}
